// src/pages/finance-portal/fixed-assets/CIPProjects.js
//
// Construction-in-Progress

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton,
    LinearProgress, MenuItem, Paper, Skeleton, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ConstructionIcon from '@mui/icons-material/Construction';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';

import axiosInstance from '../../../utils/axiosInstance';
import {
    FA_FONT, FA_ACCENT, FA_ACCENT_DARK, CIP_STATUS_COLORS,
    formatCurrency, formatDate, statusChip, humanizeStatus,
} from './components/fixedAssetFormatters';

const headSx = { fontFamily: FA_FONT, fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' };
const cellSx = { fontFamily: FA_FONT, fontSize: '0.84rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9' };

const COST_CATEGORIES = [
    'MATERIALS', 'LABOR', 'OVERHEAD', 'INTEREST', 'PERMITS', 'PROFESSIONAL', 'OTHER',
];

export default function CIPProjects() {
    const { enqueueSnackbar } = useSnackbar();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [costOpen, setCostOpen] = useState(false);
    const [capOpen, setCapOpen] = useState(false);
    const [activeProject, setActiveProject] = useState(null);
    const [activeDetail, setActiveDetail] = useState(null);

    const [createForm, setCreateForm] = useState({
        name: '', description: '', budgeted_cost: '', start_date: '', expected_completion_date: '',
        target_useful_life_months: 60, notes: '',
    });
    const [createErrors, setCreateErrors] = useState({});
    const [costForm, setCostForm] = useState({
        cost_date: new Date().toISOString().slice(0, 10), category: 'MATERIALS',
        description: '', amount: '',
    });
    const [costErrors, setCostErrors] = useState({});
    const [capForm, setCapForm] = useState({
        asset_name: '', in_service_date: new Date().toISOString().slice(0, 10),
        useful_life_months: 60,
    });
    const [capErrors, setCapErrors] = useState({});

    const setCreateField = (k, v) => {
        setCreateForm(prev => ({ ...prev, [k]: v }));
        if (createErrors[k]) setCreateErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
    };
    const setCostField = (k, v) => {
        setCostForm(prev => ({ ...prev, [k]: v }));
        if (costErrors[k]) setCostErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
    };
    const setCapField = (k, v) => {
        setCapForm(prev => ({ ...prev, [k]: v }));
        if (capErrors[k]) setCapErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
    };

    const validateCreateForm = () => {
        const e = {};
        if (!createForm.name || !createForm.name.trim()) e.name = 'Project name is required';
        else if (createForm.name.length > 255) e.name = 'Name must be 255 characters or fewer';

        if (createForm.budgeted_cost !== '' && createForm.budgeted_cost !== null) {
            const v = parseFloat(createForm.budgeted_cost);
            if (Number.isNaN(v) || v < 0) e.budgeted_cost = 'Budget must be a non-negative number';
        }
        if (createForm.target_useful_life_months !== '' && createForm.target_useful_life_months !== null) {
            const v = parseInt(createForm.target_useful_life_months, 10);
            if (!/^\d+$/.test(String(createForm.target_useful_life_months)) || v <= 0) {
                e.target_useful_life_months = 'Must be a positive whole number of months';
            }
        }
        if (createForm.start_date && createForm.expected_completion_date
            && createForm.expected_completion_date < createForm.start_date) {
            e.expected_completion_date = 'Expected completion cannot be before start date';
        }
        return e;
    };

    const validateCostForm = () => {
        const e = {};
        if (!costForm.cost_date) e.cost_date = 'Cost date is required';
        const amt = parseFloat(costForm.amount);
        if (costForm.amount === '' || costForm.amount === null) {
            e.amount = 'Amount is required';
        } else if (Number.isNaN(amt) || amt <= 0) {
            e.amount = 'Amount must be a positive number';
        }
        return e;
    };

    const validateCapForm = () => {
        const e = {};
        if (!capForm.asset_name || !capForm.asset_name.trim()) {
            e.asset_name = 'Asset name is required';
        }
        if (!capForm.in_service_date) {
            e.in_service_date = 'In-service date is required';
        }
        const life = parseInt(capForm.useful_life_months, 10);
        if (!/^\d+$/.test(String(capForm.useful_life_months)) || life <= 0) {
            e.useful_life_months = 'Useful life must be a positive whole number of months';
        }
        return e;
    };

    const fetchProjects = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/fixed-assets/cip-projects',
                { params: { per_page: 100 } });
            if (res.data?.success) setProjects(res.data.data || []);
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Failed to load CIP projects', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    useEffect(() => { fetchProjects(); }, [fetchProjects]);

    const handleCreate = async () => {
        const errs = validateCreateForm();
        if (Object.keys(errs).length > 0) {
            setCreateErrors(errs);
            enqueueSnackbar(`Fix ${Object.keys(errs).length} validation error(s)`, { variant: 'warning' });
            return;
        }
        setCreateErrors({});
        try {
            const payload = { ...createForm,
                budgeted_cost: parseFloat(createForm.budgeted_cost) || 0,
                target_useful_life_months: parseInt(createForm.target_useful_life_months, 10) || 60,
            };
            const res = await axiosInstance.post('/api/finance/fixed-assets/cip-projects', payload);
            if (res.data?.success) {
                enqueueSnackbar('CIP project created', { variant: 'success' });
                setCreateOpen(false);
                setCreateForm({ name: '', description: '', budgeted_cost: '',
                    start_date: '', expected_completion_date: '',
                    target_useful_life_months: 60, notes: '' });
                fetchProjects();
            }
        } catch (e) {
            const serverErrors = e.response?.data?.errors;
            if (serverErrors && typeof serverErrors === 'object') {
                setCreateErrors(prev => ({ ...prev, ...serverErrors }));
                enqueueSnackbar(e.response?.data?.message || 'Validation failed on server',
                    { variant: 'warning' });
            } else {
                enqueueSnackbar(e.response?.data?.message || 'Failed to create', { variant: 'error' });
            }
        }
    };

    const openProjectDetail = async (project) => {
        try {
            const res = await axiosInstance.get(`/api/finance/fixed-assets/cip-projects/${project.id}`);
            if (res.data?.success) setActiveDetail(res.data.data);
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Failed to load project', { variant: 'error' });
        }
    };

    const handleAddCost = async () => {
        if (!activeDetail) return;
        const errs = validateCostForm();
        if (Object.keys(errs).length > 0) {
            setCostErrors(errs);
            enqueueSnackbar(`Fix ${Object.keys(errs).length} validation error(s)`, { variant: 'warning' });
            return;
        }
        setCostErrors({});
        try {
            const payload = { ...costForm, amount: parseFloat(costForm.amount) };
            await axiosInstance.post(
                `/api/finance/fixed-assets/cip-projects/${activeDetail.id}/costs`, payload);
            enqueueSnackbar('Cost added', { variant: 'success' });
            setCostOpen(false);
            setCostForm({ ...costForm, amount: '', description: '' });
            openProjectDetail(activeDetail);
            fetchProjects();
        } catch (e) {
            const serverErrors = e.response?.data?.errors;
            if (serverErrors && typeof serverErrors === 'object') {
                setCostErrors(prev => ({ ...prev, ...serverErrors }));
                enqueueSnackbar(e.response?.data?.message || 'Validation failed on server',
                    { variant: 'warning' });
            } else {
                enqueueSnackbar(e.response?.data?.message || 'Failed to add cost', { variant: 'error' });
            }
        }
    };

    const handleDeleteCost = async (costId) => {
        if (!window.confirm('Remove this cost?')) return;
        try {
            await axiosInstance.delete(`/api/finance/fixed-assets/cip-projects/${activeDetail.id}/costs/${costId}`);
            openProjectDetail(activeDetail);
            fetchProjects();
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Delete failed', { variant: 'error' });
        }
    };

    const handleCapitalize = async () => {
        if (!activeDetail) return;
        const errs = validateCapForm();
        if (Object.keys(errs).length > 0) {
            setCapErrors(errs);
            enqueueSnackbar(`Fix ${Object.keys(errs).length} validation error(s)`, { variant: 'warning' });
            return;
        }
        setCapErrors({});
        if (!window.confirm(`Capitalize ${formatCurrency(activeDetail.accumulated_cost)} to a new asset?`)) return;
        try {
            const res = await axiosInstance.post(
                `/api/finance/fixed-assets/cip-projects/${activeDetail.id}/capitalize`,
                { ...capForm, useful_life_months: parseInt(capForm.useful_life_months, 10) });
            if (res.data?.success) {
                enqueueSnackbar('CIP capitalized', { variant: 'success' });
                setCapOpen(false);
                setActiveDetail(null);
                fetchProjects();
            }
        } catch (e) {
            const serverErrors = e.response?.data?.errors;
            if (serverErrors && typeof serverErrors === 'object') {
                setCapErrors(prev => ({ ...prev, ...serverErrors }));
                enqueueSnackbar(e.response?.data?.message || 'Validation failed on server',
                    { variant: 'warning' });
            } else {
                enqueueSnackbar(e.response?.data?.message || 'Capitalize failed', { variant: 'error' });
            }
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: `linear-gradient(135deg, ${FA_ACCENT} 0%, ${FA_ACCENT_DARK} 100%)`,
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: 3, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ConstructionIcon sx={{ fontSize: 30, color: '#fff' }} />
                        <Typography sx={{ fontFamily: FA_FONT, fontWeight: 800, color: 'white', fontSize: '1.6rem' }}>
                            Construction-in-Progress
                        </Typography>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
                        sx={{ fontFamily: FA_FONT, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'white', color: FA_ACCENT, '&:hover': { bgcolor: 'rgba(255,255,255,0.92)' } }}>
                        New Project
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1300, mx: 'auto' }}>
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Project #', 'Name', 'Status', 'Budget', 'Accumulated', 'Variance', '% Done', 'Actions'].map(h => (
                                        <TableCell key={h} sx={headSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? [1, 2, 3].map(i => (
                                    <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7, 8].map(j => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
                                )) : projects.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8' }}>No CIP projects yet</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : projects.map(p => (
                                    <TableRow key={p.id} hover>
                                        <TableCell sx={{ ...cellSx, fontWeight: 600, color: FA_ACCENT_DARK }}>{p.project_number}</TableCell>
                                        <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{p.name}</TableCell>
                                        <TableCell sx={cellSx}>
                                            <Chip size="small" label={humanizeStatus(p.status)}
                                                sx={{ fontFamily: FA_FONT, fontWeight: 600, ...statusChip(p.status, CIP_STATUS_COLORS) }} />
                                        </TableCell>
                                        <TableCell sx={cellSx}>{formatCurrency(p.budgeted_cost)}</TableCell>
                                        <TableCell sx={cellSx}>{formatCurrency(p.accumulated_cost)}</TableCell>
                                        <TableCell sx={{ ...cellSx, color: p.variance > 0 ? '#dc2626' : '#166534' }}>
                                            {formatCurrency(p.variance)}
                                        </TableCell>
                                        <TableCell sx={cellSx} style={{ minWidth: 120 }}>
                                            <LinearProgress variant="determinate" value={p.percent_complete}
                                                sx={{ height: 8, borderRadius: 6 }} />
                                            <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.72rem', mt: 0.4 }}>
                                                {p.percent_complete}%
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={cellSx}>
                                            <Tooltip title="Detail">
                                                <IconButton size="small" onClick={() => openProjectDetail(p)}>
                                                    <VisibilityIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            {/* Create Dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily: FA_FONT, fontWeight: 700 }}>New CIP Project</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Project Name *" required
                                value={createForm.name}
                                onChange={e => setCreateField('name', e.target.value)}
                                error={Boolean(createErrors.name)} helperText={createErrors.name || ' '}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" multiline minRows={2} label="Description"
                                value={createForm.description}
                                onChange={e => setCreateField('description', e.target.value)}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" type="number" label="Budgeted Cost"
                                value={createForm.budgeted_cost}
                                onChange={e => setCreateField('budgeted_cost', e.target.value)}
                                error={Boolean(createErrors.budgeted_cost)}
                                helperText={createErrors.budgeted_cost || ' '}
                                inputProps={{ min: 0, step: '0.01' }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" type="number" label="Target Useful Life (months)"
                                value={createForm.target_useful_life_months}
                                onChange={e => setCreateField('target_useful_life_months', e.target.value)}
                                error={Boolean(createErrors.target_useful_life_months)}
                                helperText={createErrors.target_useful_life_months || ' '}
                                inputProps={{ min: 0, step: 1 }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" type="date" label="Start Date"
                                value={createForm.start_date}
                                onChange={e => setCreateField('start_date', e.target.value)}
                                helperText=" "
                                InputLabelProps={{ shrink: true, sx: { fontFamily: FA_FONT } }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" type="date" label="Expected Completion"
                                value={createForm.expected_completion_date}
                                onChange={e => setCreateField('expected_completion_date', e.target.value)}
                                error={Boolean(createErrors.expected_completion_date)}
                                helperText={createErrors.expected_completion_date || ' '}
                                InputLabelProps={{ shrink: true, sx: { fontFamily: FA_FONT } }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCreateOpen(false)} sx={{ fontFamily: FA_FONT, textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleCreate} variant="contained"
                        sx={{ fontFamily: FA_FONT, textTransform: 'none', bgcolor: FA_ACCENT,
                            '&:hover': { bgcolor: FA_ACCENT_DARK }, borderRadius: '10px' }}>Create</Button>
                </DialogActions>
            </Dialog>

            {/* Detail Dialog */}
            <Dialog open={Boolean(activeDetail)} onClose={() => setActiveDetail(null)} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily: FA_FONT, fontWeight: 700 }}>
                    {activeDetail?.project_number}: {activeDetail?.name}
                </DialogTitle>
                <DialogContent>
                    {activeDetail && (
                        <Box>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6} md={3}>
                                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.72rem', color: '#94a3b8', fontWeight: 700 }}>BUDGET</Typography>
                                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '1rem', fontWeight: 600 }}>{formatCurrency(activeDetail.budgeted_cost)}</Typography>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.72rem', color: '#94a3b8', fontWeight: 700 }}>ACCUMULATED</Typography>
                                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '1rem', fontWeight: 600 }}>{formatCurrency(activeDetail.accumulated_cost)}</Typography>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.72rem', color: '#94a3b8', fontWeight: 700 }}>VARIANCE</Typography>
                                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '1rem', fontWeight: 600,
                                        color: activeDetail.variance > 0 ? '#dc2626' : '#166534' }}>
                                        {formatCurrency(activeDetail.variance)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.72rem', color: '#94a3b8', fontWeight: 700 }}>STATUS</Typography>
                                    <Chip size="small" label={humanizeStatus(activeDetail.status)}
                                        sx={{ fontFamily: FA_FONT, fontWeight: 600, ...statusChip(activeDetail.status, CIP_STATUS_COLORS) }} />
                                </Grid>
                            </Grid>

                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <Button size="small" startIcon={<AddIcon />}
                                    disabled={activeDetail.status === 'CAPITALIZED'}
                                    onClick={() => setCostOpen(true)}
                                    sx={{ fontFamily: FA_FONT, textTransform: 'none' }}>
                                    Add Cost
                                </Button>
                                <Button size="small" variant="contained" startIcon={<DoneAllIcon />}
                                    disabled={activeDetail.status === 'CAPITALIZED'
                                        || (activeDetail.accumulated_cost || 0) <= 0}
                                    onClick={() => { setCapForm(f => ({ ...f, asset_name: activeDetail.name })); setCapOpen(true); }}
                                    sx={{ fontFamily: FA_FONT, textTransform: 'none', bgcolor: FA_ACCENT,
                                        '&:hover': { bgcolor: FA_ACCENT_DARK }, borderRadius: '10px' }}>
                                    Capitalize
                                </Button>
                            </Box>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {['Date', 'Category', 'Description', 'Amount', ''].map(h => (
                                                <TableCell key={h} sx={headSx}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(activeDetail.costs || []).map(c => (
                                            <TableRow key={c.id}>
                                                <TableCell sx={cellSx}>{formatDate(c.cost_date)}</TableCell>
                                                <TableCell sx={cellSx}>{humanizeStatus(c.category)}</TableCell>
                                                <TableCell sx={cellSx}>{c.description || '\u2014'}</TableCell>
                                                <TableCell sx={cellSx}>{formatCurrency(c.amount)}</TableCell>
                                                <TableCell sx={cellSx}>
                                                    {activeDetail.status !== 'CAPITALIZED' && (
                                                        <IconButton size="small" onClick={() => handleDeleteCost(c.id)}>
                                                            <DeleteIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setActiveDetail(null)} sx={{ fontFamily: FA_FONT, textTransform: 'none' }}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Add Cost Dialog */}
            <Dialog open={costOpen} onClose={() => setCostOpen(false)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily: FA_FONT, fontWeight: 700 }}>Add Cost</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" type="date" label="Date *" required
                                value={costForm.cost_date}
                                onChange={e => setCostField('cost_date', e.target.value)}
                                error={Boolean(costErrors.cost_date)} helperText={costErrors.cost_date || ' '}
                                InputLabelProps={{ shrink: true, sx: { fontFamily: FA_FONT } }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" select label="Category"
                                value={costForm.category}
                                onChange={e => setCostField('category', e.target.value)}
                                helperText=" "
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                {COST_CATEGORIES.map(c => <MenuItem key={c} value={c} sx={{ fontFamily: FA_FONT }}>{humanizeStatus(c)}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Description"
                                value={costForm.description}
                                onChange={e => setCostField('description', e.target.value)}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" type="number" label="Amount *" required
                                value={costForm.amount}
                                onChange={e => setCostField('amount', e.target.value)}
                                error={Boolean(costErrors.amount)} helperText={costErrors.amount || ' '}
                                inputProps={{ min: 0, step: '0.01' }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCostOpen(false)} sx={{ fontFamily: FA_FONT }}>Cancel</Button>
                    <Button onClick={handleAddCost} variant="contained"
                        sx={{ fontFamily: FA_FONT, textTransform: 'none', bgcolor: FA_ACCENT,
                            '&:hover': { bgcolor: FA_ACCENT_DARK }, borderRadius: '10px' }}>Add</Button>
                </DialogActions>
            </Dialog>

            {/* Capitalize Dialog */}
            <Dialog open={capOpen} onClose={() => setCapOpen(false)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily: FA_FONT, fontWeight: 700 }}>Capitalize CIP</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Asset Name *" required
                                value={capForm.asset_name}
                                onChange={e => setCapField('asset_name', e.target.value)}
                                error={Boolean(capErrors.asset_name)} helperText={capErrors.asset_name || ' '}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" type="date" label="In-Service Date *" required
                                value={capForm.in_service_date}
                                onChange={e => setCapField('in_service_date', e.target.value)}
                                error={Boolean(capErrors.in_service_date)}
                                helperText={capErrors.in_service_date || ' '}
                                InputLabelProps={{ shrink: true, sx: { fontFamily: FA_FONT } }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" type="number" label="Useful Life (months) *" required
                                value={capForm.useful_life_months}
                                onChange={e => setCapField('useful_life_months', e.target.value)}
                                error={Boolean(capErrors.useful_life_months)}
                                helperText={capErrors.useful_life_months || ' '}
                                inputProps={{ min: 1, step: 1 }}
                                InputProps={{ sx: { fontFamily: FA_FONT } }}
                                InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCapOpen(false)} sx={{ fontFamily: FA_FONT }}>Cancel</Button>
                    <Button onClick={handleCapitalize} variant="contained"
                        sx={{ fontFamily: FA_FONT, textTransform: 'none', bgcolor: FA_ACCENT,
                            '&:hover': { bgcolor: FA_ACCENT_DARK }, borderRadius: '10px' }}>Capitalize</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
